import React, { useCallback, useEffect, useRef, useState } from 'react';
import OperationsTvHeader from './operationsTv/OperationsTvHeader';
import OperationsTvTable from './operationsTv/OperationsTvTable';
import OperationsTvFooter from './operationsTv/OperationsTvFooter';
import { useOperationsTvBoard } from '../hooks/useOperationsTvBoard';

const OperationsTvBoard: React.FC<{ tvMode?: boolean; publicToken?: string | null; publicAlias?: string | null }> = ({ tvMode = false, publicToken = null, publicAlias = null }) => {
  const [now, setNow] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const tvRootRef = useRef<HTMLElement | null>(null);
  const { rows, loading, trackerError, summary, health, lastRefresh, refreshMs } = useOperationsTvBoard({ publicToken, publicAlias });

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const updateFullscreenState = useCallback(() => {
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
      msFullscreenElement?: Element | null;
    };
    const fullEl = doc.fullscreenElement || doc.webkitFullscreenElement || doc.msFullscreenElement || null;
    const root = tvRootRef.current;
    setIsFullscreen(!!fullEl && !!root && fullEl === root);
  }, []);

  useEffect(() => {
    const onFs = () => updateFullscreenState();
    document.addEventListener('fullscreenchange', onFs);
    document.addEventListener('webkitfullscreenchange', onFs as EventListener);
    document.addEventListener('MSFullscreenChange', onFs as EventListener);
    updateFullscreenState();
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      document.removeEventListener('webkitfullscreenchange', onFs as EventListener);
      document.removeEventListener('MSFullscreenChange', onFs as EventListener);
    };
  }, [updateFullscreenState]);

  const toggleFullscreen = useCallback(async () => {
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
      msFullscreenElement?: Element | null;
      webkitExitFullscreen?: () => Promise<void> | void;
      msExitFullscreen?: () => Promise<void> | void;
    };
    const root = tvRootRef.current as (HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void> | void;
      msRequestFullscreen?: () => Promise<void> | void;
    }) | null;
    if (!root) return;

    const currentFsEl = doc.fullscreenElement || doc.webkitFullscreenElement || doc.msFullscreenElement || null;
    const isCurrentRootFullscreen = currentFsEl === root;

    try {
      if (isCurrentRootFullscreen) {
        if (doc.exitFullscreen) await doc.exitFullscreen();
        else if (doc.webkitExitFullscreen) await doc.webkitExitFullscreen();
        else if (doc.msExitFullscreen) await doc.msExitFullscreen();
        return;
      }

      if (root.requestFullscreen) await root.requestFullscreen();
      else if (root.webkitRequestFullscreen) await root.webkitRequestFullscreen();
      else if (root.msRequestFullscreen) await root.msRequestFullscreen();
    } catch {
      // Ignore browser-specific fullscreen errors; state is synced by fullscreenchange listener.
    }
  }, []);

  return (
    <section
      ref={tvRootRef}
      className={`ops-tv-canvas relative overflow-hidden ${
        tvMode
          ? 'h-full w-full min-h-0 rounded-none border-0 p-5 lg:p-7'
          : 'rounded-[2rem] border border-slate-700/80 p-4 lg:p-6 shadow-[0_40px_120px_rgba(2,6,23,0.85)]'
      }`}
    >
      <div className="pointer-events-none absolute -left-24 top-1/3 h-72 w-72 rounded-full bg-sky-500/10 blur-[90px]" />
      <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-amber-300/10 blur-[90px]" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(115deg,transparent_0%,rgba(255,255,255,0.03)_45%,transparent_100%)]" />

      <div className="relative space-y-5">
        <OperationsTvHeader
          summary={summary}
          health={health}
          now={now}
          tvMode={tvMode}
          isFullscreen={isFullscreen}
          onToggleFullscreen={toggleFullscreen}
          lastRefresh={lastRefresh}
        />
        <OperationsTvTable rows={rows} loading={loading} tvMode={tvMode} />
        <OperationsTvFooter health={health} lastRefresh={lastRefresh} refreshMs={refreshMs} trackerError={trackerError} />
      </div>
    </section>
  );
};

export default OperationsTvBoard;
